// Pure helpers (no browser or app imports) that decide when the location sync
// can reuse a previously resolved place instead of paying for the Google
// Geocoding + Places Autocomplete + Place Details chain again.
//
// Only our own compendium fields and the device coordinates are cached. No
// Google Places content (such as fromGoogle details) is stored.

export const REUSE_RADIUS_KM = 1
export const RESOLVED_MAX_AGE_MS = 24 * 60 * 60 * 1000
export const FAILED_RETRY_AFTER_MS = 30 * 60 * 1000

const EARTH_RADIUS_KM = 6371

export const distanceKm = (a, b) => {
    const toRad = (deg) => (deg * Math.PI) / 180
    const dLat = toRad(b.latitude - a.latitude)
    const dLng = toRad(b.longitude - a.longitude)
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.latitude)) *
            Math.cos(toRad(b.latitude)) *
            Math.sin(dLng / 2) ** 2
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h))
}

/**
 * @param {{latitude:number, longitude:number, resolvedAt:number, place:Object|null}|null} cached
 * @param {{latitude:number, longitude:number}} coordinates
 * @param {number} now - epoch ms
 * @returns {{reuse:boolean, place:Object|null}} reuse=false means resolve again
 */
export const readResolvedPlace = (cached, coordinates, now = Date.now()) => {
    if (
        !cached ||
        !Number.isFinite(cached.latitude) ||
        !Number.isFinite(cached.longitude) ||
        !Number.isFinite(cached.resolvedAt)
    ) {
        return { reuse: false, place: null }
    }

    const ageMs = now - cached.resolvedAt
    if (ageMs < 0) return { reuse: false, place: null }
    if (distanceKm(cached, coordinates) > REUSE_RADIUS_KM) {
        return { reuse: false, place: null }
    }

    // A failed resolution is remembered only briefly, so page reloads at the
    // same spot do not re-run the paid chain, but a later retry still happens.
    const maxAge = cached.place ? RESOLVED_MAX_AGE_MS : FAILED_RETRY_AFTER_MS
    if (ageMs > maxAge) return { reuse: false, place: null }

    return { reuse: true, place: cached.place ?? null }
}

/** Only our own compendium fields, never Google place details. */
export const toCachedPlace = (place) =>
    place?.id != null && place?.name && place?.slug
        ? {
              id: place.id,
              name: place.name,
              slug: place.slug,
              country: place.country ?? "",
              country_code: place.country_code ?? "",
              overall_safety: place.overall_safety ?? null,
          }
        : null
